import type { FC, FormEvent } from 'react';

import useEffectOnce from '@app/hooks/useEffectOnce';

import { InputErrorMessage } from '@lokalise/louis';
import {
    AuthInput,
    AuthLabel,
    Button,
    ButtonWrapper,
    Column,
    CommonError,
    Error,
    FooterLinks,
    FormRow,
    Github,
    Google,
    Header,
    IconButton,
    Illustration,
    Label,
    Layout,
    Link,
    Microsoft,
    PasswordInput,
    Separator,
    SocialButtons,
    SocialButtonsLabelWrapper,
    SocialButtonsWrapper,
} from '../../components';
import { getErrorForField, isCommonError } from '../../helpers';
import { Network } from '../../types';
import type { ResponseError } from '../../types';
import Login1xJpg from './assets/login.jpg';
import Login1xWebp from './assets/login.webp';
import Login15xJpg from './assets/login@1_5x.jpg';
import Login15xWebp from './assets/login@1_5x.webp';
import Login2xJpg from './assets/login@2x.jpg';
import Login2xWebp from './assets/login@2x.webp';
import type { LoginFormDataType } from './types';

interface Props {
    loading: boolean;
    buttonText: string;
    formData: LoginFormDataType;
    error: ResponseError | null;
    onNetworkClick: (network: Network) => void;
    onFormDataChange: (data: Record<string, string>) => void;
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
    onResetPassClick: () => void;
}

const LoginPage: FC<Props> = ({
    buttonText,
    loading,
    formData,
    error,
    onNetworkClick,
    onFormDataChange,
    onSubmit,
    onResetPassClick,
}) => {
    useEffectOnce(() => {
        // logout clears session, needed for project setup redirect logic
        if (sessionStorage.getItem('notReturnedUser')) {
            sessionStorage.removeItem('notReturnedUser');
        }
    });

    return (
        <Layout nested>
            <Column hidden>
                <picture>
                    <source srcSet={`${Login1xWebp} 1x, ${Login15xWebp} 1.5x, ${Login2xWebp} 2x, `} type="image/webp" />
                    <source srcSet={`${Login1xJpg} 1x, ${Login15xJpg} 1.5x, ${Login2xJpg} 2x, `} type="image/jpg" />
                    <Illustration src={Login15xJpg} />
                </picture>
            </Column>
            <Column>
                <Header>Log in</Header>
                <SocialButtons>
                    <SocialButtonsLabelWrapper>
                        <Label>Log in with</Label>
                    </SocialButtonsLabelWrapper>
                    <SocialButtonsWrapper>
                        <IconButton aria-label="google" onClick={() => onNetworkClick(Network.google)}>
                            <Google />
                        </IconButton>
                        <IconButton aria-label="github" onClick={() => onNetworkClick(Network.github)}>
                            <Github />
                        </IconButton>
                        <IconButton aria-label="microsoft" onClick={() => onNetworkClick(Network.microsoft)}>
                            <Microsoft />
                        </IconButton>
                    </SocialButtonsWrapper>
                </SocialButtons>
                <Separator>Or log in with your email</Separator>
                {isCommonError(error) && (
                    <CommonError>
                        <Error>{error?.message}</Error>
                    </CommonError>
                )}
                <form onSubmit={onSubmit} autoComplete="on">
                    <FormRow>
                        <AuthLabel text="Work e-mail" />
                        <AuthInput
                            autoComplete="email"
                            id="email"
                            name="name"
                            placeholder="user@company.com"
                            value={formData.email}
                            onChange={(e) => onFormDataChange({ email: e.target.value })}
                        />
                        {getErrorForField(error, 'email')?.map((msg) => (
                            <InputErrorMessage key={msg} id="emailError">
                                {msg}
                            </InputErrorMessage>
                        ))}
                    </FormRow>
                    <FormRow>
                        <Label>Password</Label>
                        <PasswordInput
                            value={formData.password}
                            onChange={(e) => onFormDataChange({ password: e.target.value })}
                            passwordType="current-password"
                        />
                        {getErrorForField(error, 'password')?.map((msg) => (
                            <InputErrorMessage key={msg} id="passwordError">
                                {msg}
                            </InputErrorMessage>
                        ))}
                    </FormRow>
                    <ButtonWrapper>
                        <Button type="submit" variant="primary" loading={loading}>
                            {buttonText}
                        </Button>
                    </ButtonWrapper>
                </form>
                <FooterLinks $center>
                    <Link $bold href="/sso">
                        Log in with SSO
                    </Link>
                </FooterLinks>
                <FooterLinks>
                    <Link href="/signup">Not registered? Sign up</Link>
                    <Link onClick={onResetPassClick}>Forgot password?</Link>
                </FooterLinks>
            </Column>
        </Layout>
    );
};

export default LoginPage;
